import queryString from 'query-string';

import { storableError } from '../../util/errors';
import { userTypeOf } from '../../util/userType';
import { newIdempotencyKey } from '../../util/idempotencyKey';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchNetworkEntries, networkAction, fetchNetworkActivity, downloadNetworkEntriesCsv } from '../../util/api';
import { talentSideFor, supplierListSideFor, inviteeSideFor, sectionFromSearch, CUSTOMERS_SECTIONS } from '../PreferredNetwork/networkPages';

/**
 * The Preferred Talent page: one page of the organisation's talent (a supplier's consultants),
 * with the count of every status and the people's profiles, read for the filters in the address.
 * Actions on entries go to the server one at a time and the page is read again afterwards.
 */

export const PER_PAGE = 24;
const LIST_KEYS = ['status', 'roles', 'tags'];
const STATUSES = ['requested', 'accepted', 'rejected', 'expired', 'ended'];
const SORTS = ['added', 'name', 'status'];

// ================ Action types ================ //

export const FETCH_ENTRIES_REQUEST = 'app/PreferredTalentPage/FETCH_ENTRIES_REQUEST';
export const FETCH_ENTRIES_SUCCESS = 'app/PreferredTalentPage/FETCH_ENTRIES_SUCCESS';
export const FETCH_ENTRIES_ERROR = 'app/PreferredTalentPage/FETCH_ENTRIES_ERROR';

// ================ Reducer ================ //

const emptyCounts = { invited: 0, linked: 0, declined: 0, expired: 0, removed: 0 };

const initialState = {
  status: 'idle',
  // The query the entries on screen answer, as a key, so the same query is not asked twice at once.
  queryKey: null,
  items: [],
  total: 0,
  counts: emptyCounts,
  listingIds: [],
  // Whether the organisation shows this list on its public profile, per role; null when not answered.
  publicDisplay: null,
  // The consent an invitee is asked for on their own page; null on an organisation's list.
  consent: null,
  // What the invitee wants of job and RFP alerts, on their own page; null when not answered.
  alertPreference: null,
  // How the organisation's members other than managers change its lists: directly, or by approval.
  memberPolicy: 'direct',
  // The requests waiting for a manager: all of them for a manager, a member's own otherwise.
  pendingRequests: [],
  error: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ENTRIES_REQUEST:
      return { ...state, status: 'loading', queryKey: payload.queryKey, error: null };
    case FETCH_ENTRIES_SUCCESS:
      // An answer to a query the page has moved on from is not shown.
      if (payload.queryKey !== state.queryKey) return state;
      return {
        ...state,
        status: 'loaded',
        items: payload.items,
        total: payload.total,
        counts: { ...emptyCounts, ...payload.counts },
        listingIds: payload.listingIds,
        publicDisplay: payload.publicDisplay || null,
        consent: payload.consent || null,
        alertPreference: payload.alertPreference || null,
        memberPolicy: payload.memberPolicy === 'approval' ? 'approval' : 'direct',
        pendingRequests: Array.isArray(payload.pendingRequests) ? payload.pendingRequests : [],
      };
    case FETCH_ENTRIES_ERROR:
      if (payload.queryKey !== state.queryKey) return state;
      return { ...state, status: 'failed', error: payload.error };
    default:
      return state;
  }
}

// ================ Query in the address ================ //

const csv = value => (typeof value === 'string' && value ? value.split(',').filter(Boolean) : undefined);

/**
 * The list query the address asks for, on the side this user's talent page shows.
 */
export const queryFromSearch = (search, side) => {
  const raw = queryString.parse(search || '');
  let filters;
  if (typeof raw.f === 'string') {
    try {
      const parsed = JSON.parse(raw.f);
      filters = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : undefined;
    } catch (e) {
      filters = undefined;
    }
  }
  const page = Number.parseInt(raw.page, 10);
  const query = {
    side,
    q: typeof raw.q === 'string' && raw.q.trim() ? raw.q.trim().slice(0, 100) : undefined,
    status: csv(raw.status)?.filter(s => STATUSES.includes(s)),
    roles: csv(raw.roles),
    tags: csv(raw.tags),
    addedBy: typeof raw.addedBy === 'string' && raw.addedBy ? raw.addedBy : undefined,
    sort: SORTS.includes(raw.sort) ? raw.sort : undefined,
    filters,
    page: Number.isInteger(page) && page > 0 ? page : 1,
    perPage: PER_PAGE,
  };
  return Object.keys(query).reduce(
    (acc, key) => (query[key] === undefined || (Array.isArray(query[key]) && !query[key].length) ? acc : { ...acc, [key]: query[key] }),
    {}
  );
};

/** The address parameters for a list query; the side and page size are not part of the address. */
export const searchFromQuery = query => {
  const params = {};
  if (query.q) params.q = query.q;
  LIST_KEYS.forEach(key => {
    if (Array.isArray(query[key]) && query[key].length) params[key] = query[key].join(',');
  });
  if (query.addedBy) params.addedBy = query.addedBy;
  if (query.sort) params.sort = query.sort;
  if (query.filters && Object.keys(query.filters).length) params.f = JSON.stringify(query.filters);
  if (query.page && query.page > 1) params.page = query.page;
  if (query.view) params.view = query.view;
  return params;
};

const keyOf = query => JSON.stringify(query);

// ================ Thunks ================ //

/** Reads the entries for a query, unless that exact query is already being read or shown. */
export const fetchEntries = (query, { force = false } = {}) => (dispatch, getState) => {
  const queryKey = keyOf(query);
  const current = getState().PreferredTalentPage || initialState;
  if (!force && current.queryKey === queryKey && ['loading', 'loaded'].includes(current.status)) {
    return Promise.resolve(null);
  }
  dispatch({ type: FETCH_ENTRIES_REQUEST, payload: { queryKey } });
  return fetchNetworkEntries(query)
    .then(response => {
      const listings = response?.listings;
      if (listings?.data?.data?.length) dispatch(addMarketplaceEntities(listings));
      dispatch({
        type: FETCH_ENTRIES_SUCCESS,
        payload: {
          queryKey,
          items: response?.items || [],
          total: response?.total || 0,
          counts: response?.counts || {},
          listingIds: (listings?.data?.data || []).map(l => l.id),
          publicDisplay: response?.publicDisplay,
          consent: response?.consent,
          alertPreference: response?.alertPreference,
          memberPolicy: response?.memberPolicy,
          pendingRequests: response?.pendingRequests,
        },
      });
      return response;
    })
    .catch(e => {
      dispatch({ type: FETCH_ENTRIES_ERROR, payload: { queryKey, error: storableError(e) } });
      return null;
    });
};

/** Reads the current query again. */
export const refreshEntries = () => (dispatch, getState) => {
  const { queryKey } = getState().PreferredTalentPage || initialState;
  if (!queryKey) return Promise.resolve(null);
  return dispatch(fetchEntries(JSON.parse(queryKey), { force: true }));
};

// An add or a bulk invite (not a preview) is sent with a key of its own, so that if it is sent again
// it is not run twice; a key already on the body is kept.
const withIdempotencyKey = body => {
  const keyed = body?.action === 'add' || (body?.action === 'bulkInvite' && !body.preview);
  return keyed && !body.idempotencyKey ? { ...body, idempotencyKey: newIdempotencyKey() } : body;
};

/**
 * One action on the list or an entry. Resolves the server's answer; rejects with its refusal, whose
 * `error` names why (and, for someone already on the list, their `status`). The list is read again
 * after a change.
 */
export const runNetworkAction = (body, { refresh = true } = {}) => dispatch =>
  networkAction(withIdempotencyKey(body)).then(answer => {
    const preview = body.action === 'bulkInvite' && body.preview;
    if (refresh && !preview) dispatch(refreshEntries());
    return answer;
  });

/** One entry's history, newest first. */
export const fetchEntryHistory = (entryId, side) => () => fetchNetworkActivity({ entryId, side });

/** Saves the current view as a CSV file. */
export const exportEntries = query => () =>
  downloadNetworkEntriesCsv(query).then(blob => {
    if (typeof window === 'undefined' || !window.URL?.createObjectURL) return blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `preferred-network-${query.side}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return blob;
  });

/**
 * The route's data: the entries the address asks for. Without a signed-in customer or supplier
 * there is nothing to read; the page says so.
 */
export const loadData = (params, search) => (dispatch, getState) => {
  const side = talentSideFor(userTypeOf(getState().user?.currentUser));
  if (!side) return Promise.resolve(null);
  return dispatch(fetchEntries(queryFromSearch(search, side)));
};

/** The route's data for the Suppliers page: a customer's suppliers, or a supplier's sub-suppliers. */
export const loadSuppliersData = (params, search) => (dispatch, getState) => {
  const side = supplierListSideFor(userTypeOf(getState().user?.currentUser));
  if (!side) return Promise.resolve(null);
  return dispatch(fetchEntries(queryFromSearch(search, side)));
};

// The route's data for an invitee's own page: the side this type of user answers on, or nothing.
const loadInviteeData = (params, search) => (dispatch, getState) => {
  const side = inviteeSideFor(userTypeOf(getState().user?.currentUser));
  if (!side) return Promise.resolve(null);
  return dispatch(fetchEntries(queryFromSearch(search, side)));
};

/** The route's data for My Network: the companies and suppliers that invited or linked a candidate. */
export const loadMyNetworkData = (params, search) => (dispatch, getState) =>
  userTypeOf(getState().user?.currentUser) === 'candidate' ? dispatch(loadInviteeData(params, search)) : Promise.resolve(null);

/**
 * The route's data for Customers: the customers that invited or linked a supplier, or, in the section
 * the address asks for, the suppliers that source from it.
 */
export const loadCustomersData = (params, search) => (dispatch, getState) =>
  userTypeOf(getState().user?.currentUser) === 'agency'
    ? dispatch(fetchEntries(queryFromSearch(search, sectionFromSearch(CUSTOMERS_SECTIONS, search))))
    : Promise.resolve(null);
